import asyncRetry from 'async-retry';
import { recordMetric } from './newrelic';
import { initOptions } from './options';
import {
	ERROR_CODES,
	ERROR_NAMES,
	HTTP_STATUS,
	DEFAULT_OPTIONS,
} from '../constants';

export const contextKey = 'retry';

const isTransientError = (err) => {
	if (!err) return false;
	const errorName = err.name || '';
	const errorCode = err.code || '';

	const transientCodes = [
		ERROR_CODES.ETIMEDOUT,
		ERROR_CODES.ECONNRESET,
		ERROR_CODES.ENOTFOUND,
		ERROR_CODES.ECONNREFUSED,
	];
	if (transientCodes.includes(errorCode)) return true;

	const transientNames = [
		ERROR_NAMES.TIMEOUT_ERROR,
		ERROR_NAMES.ABORT_ERROR,
		ERROR_NAMES.FETCH_ERROR,
	];
	if (transientNames.includes(errorName)) return true;

	if (err.status !== undefined) {
		return (
			err.status === HTTP_STATUS.TOO_MANY_REQUESTS ||
			Math.trunc(err.status / 100) === 5
		);
	}

	return false;
};

// See: https://github.com/zeit/async-retry
const defaultOptions = {
	...DEFAULT_OPTIONS.RETRY,
	isRetryable: isTransientError,
};

export const getWrapper = (nextAction, context) => {
	initOptions(context, contextKey, defaultOptions);
	return async (fnArgs, executionContext) => {
		let attempts = 0;
		try {
			return await asyncRetry(async (bail, attemptNum) => {
				attempts = attemptNum;
				try {
					return await nextAction(fnArgs, executionContext);
				} catch (e) {
					const { isRetryable } = executionContext[contextKey].options;
					if (!(await isRetryable(e))) {
						bail(e);
						return null;
					}
					throw e;
				}
			}, executionContext[contextKey].options);
		} finally {
			executionContext[contextKey].attempts = attempts;
			recordMetric(executionContext, `${contextKey}.attempts`, attempts);
		}
	};
};

getWrapper.contextKey = contextKey;
